import {
  Autocomplete,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { createFilterOptions } from '@mui/material/Autocomplete';
import axios from 'axios';
import * as Constant from 'common/Constant';
import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { AppConfig } from '../../../../AppConfig';
const UserAccessManagementDialogDetail = ({ external, isEdit, assignedCompanyId }) => {
  const { control, getValues, setValue } = useFormContext();

  const { sysAdmin, foAdmin, mdAdmin} = useSelector((state) => state.user);
  const tenantList = useSelector((state) => state.adminUserAccess.tenantList);
  const applications = useSelector(
    (state) => state.adminUserAccess.applicationList
  );
  const [defaultTenantList, setDefaultTenantList] = useState([]);
  const [defaultRoleList, setDefaultRoleList] = useState([]);

  const externalUserMessage = 'User without Site Administrator role'

  const setTenantList = () => {
    let d = [];
    d = tenantList
      .filter((a) => a.displayText)
      .map((a) => ({
        id: a.id,
        code: a.code,
        label: a.displayText
      }));
    return d;
  };
  const setRoleList = (applicationId, external) => {
    const application = applications.filter((a) => a.id === applicationId);
    // if user is internal, filter external roles out
    if (application.length === 0) return;
    let appRoles = application[0].roles;
    if (external === false) {
      appRoles = appRoles.filter((role) => role.isExternalOnlyRole === false);
    }
    const roles = appRoles.map((a) => ({
      id: a.id,
      code: a.code,
      label: a.description
    }));
    setDefaultRoleList(roles);
  };

  const filterOptions = createFilterOptions({
    stringify: (option) => `${option.label} ${option.code}`
  });

  const emailValue = useWatch({ control, name: 'email' });
  const externalValue = useWatch({ control, name: 'external' });

  const deriveUsername = (email, isExternal) => {
    if (!email) return '';
    const username = isExternal ? email : email.split('@')[0];
    return username.substring(0, 255);
  };

  const handleExternalChange = (event) => {
    const checked = event.target.checked; // true means external
    setRoleList(getValues('applicationId'), checked);
  };

  useEffect(() => {
    if (!isEdit) {
      const isExternalUser = externalValue === undefined ? true : externalValue; // default to external when undefined
      const derived = deriveUsername(emailValue, isExternalUser);
      if (getValues('username') !== derived) {
        setValue('username', derived, { shouldValidate: true, shouldDirty: true });
      }
    }
  }, [emailValue, externalValue, isEdit, setValue, getValues]);

  useEffect(() => {
    if (tenantList && tenantList.length) {
      const tlist = setTenantList();
      setDefaultTenantList(tlist);
      if (isEdit) {
        const company = tlist.filter((t) => t.id === assignedCompanyId);
        setValue('assignedCompanyId', company.length ? company[0] : null);
      }
    }
  }, [tenantList]);



  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-firstName"
                disabled={isEdit}
                required
                fullWidth
                {...field}
                error={error}
                helperText={error ? error.message : null}
                label="First Name"
              />
            )}
            name="firstName"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-lastName"
                disabled={isEdit}
                required
                fullWidth
                {...field}
                error={error}
                helperText={error ? error.message : null}
                label="Last Name"
              />
            )}
            name="lastName"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              validate: {
                rule1: (value) => {
                  if (!isEdit && value && value.includes(' ')) {
                    return 'Username cannot have white space!';
                  }
                },
                rule2: async (value) => {
                  if (!isEdit && value) {
                    const found = await axios.get(
                      AppConfig.IAM_BACKEND_URL + `/user/username/${value}/`,
                      { hideSpinner: true }
                    );
                    if (found && found.data && found.data.userId) {
                      return 'Already exists!';
                    }
                  }
                }
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-username"
                disabled
                fullWidth  
                slotProps={{ htmlInput: { maxLength: 255 } }}              
                {...field}
                error={!!error}
                helperText={error ? error.message : 'Username auto-generated from Email'}
                label="Username"
                placeholder="Auto-generated"
                InputLabelProps={{ shrink: true }}
              />
            )}
            name="username"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            rules={{
              required: Constant.ERROR_MSG_REQUIRED_FIELD,
              validate: {
                rule1: (value) => {
                  var email = value;
                  const regEx = /^\S+@\S+\.\S+$/;
                  if (!regEx.test(email)) {
                    return 'Invalid email address';
                  }
                },
                rule2: async (value) => {
                  if (!isEdit) {
                    const found = await axios.get(
                      `${AppConfig.IAM_BACKEND_URL}/user/email`,
                      { params: { email: value }, hideSpinner: true }
                    );
                    if (found && found.data && found.data.userId) {
                      return 'Email already exists!';
                    }
                  }
                }
              }
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                id="input-emailAddress"
                disabled={isEdit}
                required
                fullWidth
                {...field}
                error={error}
                helperText={error ? error.message : null}
                label="Email Address"
              />
            )}
            name="email"
            control={control}
          />
        </Grid>
        <Grid item xs={6}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                id="input-assignedCompanyId"
                options={defaultTenantList}
                filterOptions={filterOptions}
                filterSelectedOptions
                getOptionLabel={(option) => option?.label || ''}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    {option.label}
                  </li>
                )}
                    slotProps = {{
                          listbox: {
                              sx: {
                                '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ': {
                                  color: '#000000 !important'
                                },
                                '& .MuiAutocomplete-option[aria-selected="true"]': {
                                  backgroundColor: '#5470b8 !important',
                                },
                                '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                                  backgroundColor: '#f4f5f6 !important',
                                }
                              }
                          }
                      }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Company"
                  />
                )}
                onChange={(event, data) => {
                  field.onChange(data);
                }}
                value={field.value || null}
              />
            )}
            name="assignedCompanyId"
            control={control}
          />
        </Grid>
        <Grid item xs={6}></Grid>
        {!isEdit && (
          <>
            <Grid item xs={12}>
              <Typography className="mt-4" variant="subtitle1">
                Accessibility
              </Typography>
              <Divider />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="applicationId"
                control={control}
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <FormControl fullWidth error={error}>
                      <InputLabel id="label-application-input">
                        Application
                      </InputLabel>
                      <Select
                        fullWidth
                        {...field}
                        name="applicationId"
                        labelId="label-applicationId"
                        onChange={(event) => {
                          field.onChange(event.target.value);
                          setValue('roleId', null);
                          setRoleList(event.target.value, getValues('external'));
                        }}
                        id="input-applicationId">
                        {applications &&
                          Array.isArray(applications) &&
                          applications.map((item) => {
                            return (
                              <MenuItem key={'key-' + uuid()} value={item?.id}>
                                {item.description}
                              </MenuItem>
                            );
                          })}
                      </Select>

                      <FormHelperText>
                        {error ? error.message : null}
                      </FormHelperText>
                    </FormControl>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    id="input-roleId"
                    options={defaultRoleList}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    value={
                      defaultRoleList.find(
                        (option) => option.id === field.value
                      ) || null
                    }
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    slotProps={{
                      listbox: {
                        sx: {
                          '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ':
                            {
                              color: '#000000 !important'
                            },
                          '& .MuiAutocomplete-option[aria-selected="true"]': {
                            backgroundColor: '#5470b8 !important'
                          },
                          '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                            backgroundColor: '#f4f5f6 !important'
                          }
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Role"
                        required
                        error={error}
                        helperText={error ? error.message : null}
                      />
                    )}
                    onChange={(event, data) => field.onChange(data?.id)}
                  />
                )}
                name="roleId"
                control={control}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                rules={{
                  required: Constant.ERROR_MSG_REQUIRED_FIELD
                }}
                render={({ field, fieldState: { error } }) => (
                  <Autocomplete
                    {...field}
                    multiple
                    id="input-accessibleCompanyIds"
                    filterOptions={filterOptions}
                    filterSelectedOptions
                    options={defaultTenantList}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    slotProps={{
                    listbox: {
                      sx: {
                        '& .MuiAutocomplete-option, & .MuiAutocomplete-option *, && .MuiAutocomplete-option[aria-selected="true"], & .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover ':
                          {
                            color: '#000000 !important'
                          },
                        '& .MuiAutocomplete-option[aria-selected="true"]': {
                          backgroundColor: '#5470b8 !important'
                        },
                        '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[data-focus="true"], & .MuiAutocomplete-option:hover': {
                          backgroundColor: '#f4f5f6 !important'
                        }
                      }
                    }
                      }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Accessible Company(s)"
                        required
                        error={error}
                        helperText={error ? error.message : null}
                      />
                    )}
                    onChange={(event, data) => {
                      field.onChange(data);
                    }}
                  />
                )}
                name="accessibleCompanyIds"
                control={control}
              />
            </Grid>
          </>
        )}
        {(sysAdmin || foAdmin) && (
          <Grid item lg={6}>
            <Controller
              render={({ field, fieldState: { error } }) => (
                <Tooltip title={externalUserMessage} followCursor>
                  <FormControlLabel
                    {...field}
                    control={
                      <Checkbox
                        onChange={(event) => {
                          field.onChange(event.target.checked);
                          handleExternalChange(event);
                        }}
                        color="primary"
                        checked={field.value !== undefined ? field.value : true}
                        disabled={isEdit}
                      />
                    }
                    label="External User"
                    labelPlacement="end"
                  />
                </Tooltip>
              )}
              defaultValue={true}
              control={control}
              name="external"
            />
          </Grid>)}
          {isEdit &&
            <Grid style={{ marginBottom: "10px" }} xs={12}>
              <Alert severity="warning">{Constant.EDIT_USER_WARNING_MSG}</Alert>
            </Grid>
          }
      </Grid>
    </>
  );
};

export default UserAccessManagementDialogDetail;
